import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash } from '@fortawesome/pro-light-svg-icons/faEyeSlash';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { useSmartLinkPageFacts } from '../pageActions/useSmartLinkPageFacts';
import { unpublishFactRows } from './unpublishFacts';
import { SMLUnpublishList } from './SMLUnpublishList';
import styles from './SMLUnpublishSheet.module.scss';

interface SMLUnpublishSheetProps {
  accountId: number;
  smartLink: SerializedSmartLink;
  isUnpublishing: boolean;
  closeModal: () => void;
  onUnpublish: () => void;
}

/**
 * The unpublish confirmation.
 *
 * Unpublishing does not park the page somewhere safe: it turns it back into a
 * draft, the state it was in before the artist first published. So the dialog
 * says exactly that, and then says the part that costs — the address is not
 * held for a draft, and another page can take it. The design's "the address
 * stays yours" is not true of this product, and a dialog that promises it
 * would be found out the first time somebody lost an address.
 */
const SMLUnpublishSheetComponent: React.FC<SMLUnpublishSheetProps> = ({
  accountId,
  smartLink,
  isUnpublishing,
  closeModal,
  onUnpublish,
}) => {
  const intl = useIntl();
  const facts = useSmartLinkPageFacts(accountId, smartLink);
  const { name } = smartLink.recentSmartLinkVersion;

  const rows = useMemo(
    () => unpublishFactRows({ pendingPresaveCount: facts.pendingPresaveCount, viewCount: facts.viewCount }),
    [facts.pendingPresaveCount, facts.viewCount]
  );

  if (facts.isLoading) {
    return (
      <div className={styles.sheet}>
        <LoadingView />
      </div>
    );
  }

  return (
    <div className={styles.sheet}>
      <div className={styles.header}>
        <span className={styles.mark}>
          <FontAwesomeIcon icon={faEyeSlash} />
        </span>
        <div className={styles.text}>
          <h2 className={styles.title}>
            {intl.formatMessage({ id: 'app.sml.unpublish.title', defaultMessage: 'Unpublish “{name}”?' }, { name })}
          </h2>
          <p className={styles.description}>
            {intl.formatMessage({
              id: 'app.sml.unpublish.description',
              defaultMessage:
                'The page goes offline now and becomes a draft again — exactly as it was before you first published it. Everything on it is kept.',
            })}
          </p>
        </div>
      </div>

      <SMLUnpublishList rows={rows} shareUrl={smartLink.shareUrl} releaseDate={facts.releaseDate} />

      <p className={styles.fineprint}>
        {intl.formatMessage({
          id: 'app.sml.unpublish.fineprint',
          defaultMessage: 'Publish it again from the editor whenever you want.',
        })}
      </p>

      <div className={styles.footer}>
        <button type="button" className={styles.cancel} onClick={closeModal}>
          {intl.formatMessage({ id: 'app.sml.unpublish.cancel', defaultMessage: 'Cancel' })}
        </button>
        <button type="button" className={styles.confirm} onClick={onUnpublish} disabled={isUnpublishing}>
          {intl.formatMessage({ id: 'app.sml.unpublish.confirm', defaultMessage: 'Unpublish page' })}
        </button>
      </div>
    </div>
  );
};

export const SMLUnpublishSheet = TypedMemo(SMLUnpublishSheetComponent);
